/**
 * User Approvals Page
 * Allows manufacturers and parent users to approve/reject pending user registrations
 */

import { useEffect, useMemo, useState } from 'react';
import { userService, PendingUser } from '../services/user.service';
import { hierarchyService } from '../services/hierarchy.service';
import { Alert, Button, FilterBar, Modal, PageHeader, StatusBadge } from '../components/ui';

export default function UserApprovalsPage() {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  
  // Reject modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Role names cache
  const [roleNames, setRoleNames] = useState<{ [key: number]: string }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'created-desc' | 'created-asc'>('created-desc');

  useEffect(() => {
    loadPendingApprovals();
    loadRoleNames();
  }, []);

  const loadPendingApprovals = async () => {
    try {
      setLoading(true);
      setError(null);
      const users = await userService.getPendingApprovals();
      setPendingUsers(users);
    } catch (err: any) {
      console.error('Failed to load pending approvals:', err);
      setError(err.response?.data?.error || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const loadRoleNames = async () => {
    try {
      const config = await hierarchyService.getConfig();
      const names: { [key: number]: string } = {};
      config.levels.forEach(level => {
        names[level.levelNumber] = level.roleName;
      });
      setRoleNames(names);
    } catch (err) {
      console.error('Failed to load role names:', err);
    }
  };

  const handleApprove = async (user: PendingUser) => {
    try {
      setProcessing(user.id);
      setError(null);
      setSuccess(null);

      await userService.approveUser(user.id);
      
      setSuccess(`${user.fullName} has been approved successfully!`);
      setPendingUsers(pendingUsers.filter(u => u.id !== user.id));
    } catch (err: any) {
      console.error('Failed to approve user:', err);
      setError(err.response?.data?.error || 'Failed to approve user');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectClick = (user: PendingUser) => {
    setSelectedUser(user);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedUser) return;

    try {
      setProcessing(selectedUser.id);
      setError(null);
      setSuccess(null);

      await userService.rejectUser(selectedUser.id, rejectReason);
      
      setSuccess(`${selectedUser.fullName}'s registration has been rejected.`);
      setPendingUsers(pendingUsers.filter(u => u.id !== selectedUser.id));
      setShowRejectModal(false);
      setSelectedUser(null);
      setRejectReason('');
    } catch (err: any) {
      console.error('Failed to reject user:', err);
      setError(err.response?.data?.error || 'Failed to reject user');
    } finally {
      setProcessing(null);
    }
  };

  const getRoleName = (levelNumber: number): string => {
    return roleNames[levelNumber] || `Level ${levelNumber}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const visibleUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    const filtered = pendingUsers.filter((user) => {
      if (!query) return true;
      const roleLabel = roleNames[user.roleLevel] || `Level ${user.roleLevel}`;
      return (
        user.fullName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.phone.toLowerCase().includes(query) ||
        roleLabel.toLowerCase().includes(query)
      );
    });

    return filtered.sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return sortBy === 'created-asc' ? aTime - bTime : bTime - aTime;
    });
  }, [pendingUsers, searchTerm, sortBy, roleNames]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pending approvals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHeader
        title="User Approvals"
        description="Review and approve pending user registrations."
        badge={
          <StatusBadge tone={pendingUsers.length > 0 ? 'suspicious' : 'active'}>
            {pendingUsers.length > 0 ? `${pendingUsers.length} Pending` : 'No Pending Items'}
          </StatusBadge>
        }
      />

        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

      {pendingUsers.length > 0 && (
        <FilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search pending users by name, email, phone, or role..."
          sortOptions={[
            { value: 'created-desc', label: 'Sort: Latest Registered' },
            { value: 'created-asc', label: 'Sort: Oldest Registered' },
          ]}
          selectedSort={sortBy}
          onSortChange={(value) => setSortBy(value as 'created-desc' | 'created-asc')}
        />
      )}

      {/* Pending Users List */}
      {pendingUsers.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Approvals</h3>
            <p className="text-gray-600">All user registrations have been processed.</p>
        </div>
      ) : visibleUsers.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Match This Search</h3>
          <p className="text-gray-600 mb-4">Try a different search term or clear the filter.</p>
          <Button variant="outline" onClick={() => setSearchTerm('')}>
            Clear Search
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Pending Registrations ({visibleUsers.length})
              </h2>
            </div>
            
            <div className="divide-y divide-gray-200">
              {visibleUsers.map((user) => (
                <div key={user.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">{user.fullName}</h3>
                        <StatusBadge tone="anchored" size="sm">
                          {getRoleName(user.roleLevel)}
                        </StatusBadge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>{user.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>{user.phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Registered {formatDate(user.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleApprove(user)}
                        disabled={processing === user.id}
                        className="flex items-center gap-2"
                      >
                        {processing === user.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleRejectClick(user)}
                        disabled={processing === user.id}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && selectedUser && (
        <Modal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedUser(null);
            setRejectReason('');
          }}
          title="Reject User Registration"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to reject <strong>{selectedUser.fullName}</strong>'s registration?
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span id="reject-reason-label">Reason (Optional)</span>
              </label>
              <textarea
                aria-labelledby="reject-reason-label"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejection..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedUser(null);
                  setRejectReason('');
                }}
                disabled={processing === selectedUser.id}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectConfirm}
                disabled={processing === selectedUser.id}
                className="bg-red-600 hover:bg-red-700"
              >
                {processing === selectedUser.id ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
